/**
 * Schema.org structured data generators for SEO
 */

export interface LocalBusinessSchemaProps {
  name?: string;
  description?: string;
  url?: string;
  telephone?: string;
  email?: string;
  priceRange?: string;
  areaServed?: Array<{ name: string; addressRegion: string }>;
}

export const generateLocalBusinessSchema = (props?: LocalBusinessSchemaProps) => {
  const defaults = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": "https://ricksblackcarservicesantonio.com/#business",
    "name": "Rick's Black Car Service",
    "url": "https://ricksblackcarservicesantonio.com/",
    "logo": "https://ricksblackcarservicesantonio.com/og-image.jpg",
    "image": "https://ricksblackcarservicesantonio.com/og-image.jpg",
    "telephone": "+1-210-296-7409",
    "email": "ricksblackcarservice@outlook.com",
    "priceRange": "$$",
    "description": props?.description || "5-star black car and chauffeur service in San Antonio. SAT airport pickups, corporate travel, weddings, and Hill Country wine tours. Book 24/7.",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "16034 Rough Oak Street",
      "addressLocality": "San Antonio",
      "addressRegion": "TX",
      "postalCode": "78232",
      "addressCountry": "US"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 29.5850,
      "longitude": -98.4615
    },
    "openingHoursSpecification": [{
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
      "opens": "00:00",
      "closes": "23:59"
    }],
    "areaServed": props?.areaServed || [
      { "@type": "City", "name": "San Antonio", "addressRegion": "Texas" },
      { "@type": "City", "name": "Boerne", "addressRegion": "Texas" },
      { "@type": "City", "name": "New Braunfels", "addressRegion": "Texas" },
      { "@type": "City", "name": "Austin", "addressRegion": "Texas" }
    ],
    "sameAs": [
      "https://share.google/GtRFwnU1H9WWSjG5l"
    ],
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "5.0",
      "reviewCount": "33",
      "bestRating": "5",
      "worstRating": "1"
    }
  };

  return {
    ...defaults,
    ...(props?.name && { name: props.name }),
    ...(props?.url && { url: props.url }),
    ...(props?.telephone && { telephone: props.telephone }),
    ...(props?.email && { email: props.email }),
    ...(props?.priceRange && { priceRange: props.priceRange })
  };
};

export interface BreadcrumbItem {
  name: string;
  url: string;
}

export const generateBreadcrumbSchema = (items: BreadcrumbItem[]) => {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": `https://ricksblackcarservicesantonio.com${item.url}`
    }))
  };
};

export const generateServiceSchema = (serviceName: string, description: string) => {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": serviceName,
    "description": description,
    "provider": {
      "@type": "LocalBusiness",
      "name": "Rick's Black Car Service",
      "url": "https://ricksblackcarservicesantonio.com/"
    },
    "areaServed": {
      "@type": "City",
      "name": "San Antonio",
      "addressRegion": "Texas"
    }
  };
};

export interface VideoObjectSchemaProps {
  name: string;
  description: string;
  thumbnailUrl?: string;
  uploadDate: string;
  contentUrl: string;
  duration?: string; // ISO 8601 duration format (e.g., "PT30S" for 30 seconds)
}

export const generateVideoObjectSchema = (props: VideoObjectSchemaProps) => {
  return {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    "name": props.name,
    "description": props.description,
    "thumbnailUrl": props.thumbnailUrl || "https://ricksblackcarservicesantonio.com/og-image.jpg",
    "uploadDate": props.uploadDate,
    "contentUrl": props.contentUrl,
    "embedUrl": "https://ricksblackcarservicesantonio.com/",
    "duration": props.duration || "PT30S",
    "publisher": {
      "@type": "Organization",
      "name": "Rick's Black Car Service",
      "logo": {
        "@type": "ImageObject",
        "url": "https://ricksblackcarservicesantonio.com/og-image.jpg",
        "width": 1200,
        "height": 630
      }
    }
  };
};

export interface ImageObjectSchemaProps {
  url: string;
  caption: string;
  width?: number;
  height?: number;
  description?: string;
}

export const generateImageObjectSchema = (props: ImageObjectSchemaProps) => {
  return {
    "@context": "https://schema.org",
    "@type": "ImageObject",
    "url": props.url,
    "caption": props.caption,
    "description": props.description || props.caption,
    "width": props.width,
    "height": props.height,
    "publisher": {
      "@type": "Organization",
      "name": "Rick's Black Car Service",
      "url": "https://ricksblackcarservicesantonio.com/"
    },
    "copyrightNotice": "© Rick's Black Car Service",
    "creditText": "Rick's Black Car Service"
  };
};

export const generateImageGallerySchema = (images: ImageObjectSchemaProps[]) => {
  return {
    "@context": "https://schema.org",
    "@type": "ImageGallery",
    "name": "Rick's Black Car Service Gallery",
    "description": "Professional photos of our luxury fleet and services in San Antonio",
    "image": images.map(img => generateImageObjectSchema(img))
  };
};
